// components/AddFamily.jsx
import { useState } from "react";
import { FaUsers, FaPlus, FaTimes } from "react-icons/fa";
import AdminService from "../services/adminService";
import LoadingForButton from "./LoadingForButton";
import { useDispatch, useSelector } from "react-redux";
import { refreshDataGuest } from "../store/guestSlice";
import UpgradePrompt from "./UpgradePrompt";

const GUEST_FREE_LIMIT = 10;

const AddFamily = ({ guests = [] }) => {
    const user = useSelector((state) => state.userStore.user);
    const dispatch = useDispatch();

    const [familyName, setFamilyName] = useState("");
    const [members, setMembers] = useState(["", ""]); // minimum 2 prazna polja

    const [errors, setErrors] = useState({ familyName: "", members: {} });
    const [loading, setLoading] = useState(false);
    const [invalidCredentials, setInvalidCredentials] = useState("");
    const [successMessage, setSuccessMessage] = useState("");

    const isPremium = user?.subscription === "premium" && user?.subscriptionStatus === "active";
    const isAtLimit = !isPremium && guests.length >= GUEST_FREE_LIMIT;

    const handleFamilyNameChange = (e) => {
        setFamilyName(e.target.value);
        setErrors((prev) => ({ ...prev, familyName: "" }));
        setInvalidCredentials("");
        setSuccessMessage("");
    };

    const handleMemberChange = (index, value) => {
        const updated = [...members];
        updated[index] = value;
        setMembers(updated);

        const newMemberErrors = { ...errors.members };
        delete newMemberErrors[index];
        setErrors((prev) => ({ ...prev, members: newMemberErrors }));
        setInvalidCredentials("");
        setSuccessMessage("");
    };

    const handleAddMember = () => {
        if (members.length >= 15) return;
        setMembers([...members, ""]);
    };

    const handleRemoveMember = (index) => {
        if (members.length <= 2) return;
        const updated = members.filter((_, i) => i !== index);
        setMembers(updated);

        const newMemberErrors = {};
        Object.entries(errors.members).forEach(([key, val]) => {
            const k = Number(key);
            if (k < index) newMemberErrors[k] = val;
            else if (k > index) newMemberErrors[k - 1] = val;
        });
        setErrors((prev) => ({ ...prev, members: newMemberErrors }));
    };

    const validate = () => {
        const newErrors = { familyName: "", members: {} };
        const nameRegex = /^[a-zA-ZčćđšžČĆĐŠŽ\s]+$/;
        const familyNameRegex = /^[a-zA-ZčćđšžČĆĐŠŽ0-9\s\-]+$/;

        const trimmedFamilyName = familyName.trim();
        if (!trimmedFamilyName) {
            newErrors.familyName = "Naziv obitelji je obavezan.";
        } else if (trimmedFamilyName.length < 3 || trimmedFamilyName.length > 60) {
            newErrors.familyName = "Naziv mora biti između 3 i 60 znakova.";
        } else if (!familyNameRegex.test(trimmedFamilyName)) {
            newErrors.familyName = "Naziv sadrži nedopuštene znakove.";
        }

        const seen = new Set();
        members.forEach((m, idx) => {
            const trimmed = m.trim();
            if (!trimmed) {
                newErrors.members[idx] = "Ime je obavezno.";
                return;
            }
            if (trimmed.length > 50) {
                newErrors.members[idx] = "Ime ne smije biti duže od 50 znakova.";
                return;
            }
            const wordCount = trimmed.split(/\s+/).length;
            if (wordCount < 2 || wordCount > 4) {
                newErrors.members[idx] = "Ime mora sadržavati 2-4 riječi.";
                return;
            }
            if (!nameRegex.test(trimmed)) {
                newErrors.members[idx] = "Ime smije sadržavati samo slova.";
                return;
            }
            const lower = trimmed.toLowerCase();
            if (seen.has(lower)) {
                newErrors.members[idx] = "Ovo ime je već dodano.";
                return;
            }
            seen.add(lower);
        });

        setErrors(newErrors);

        const hasFamilyNameError = !!newErrors.familyName;
        const hasMemberErrors = Object.keys(newErrors.members).length > 0;
        return !hasFamilyNameError && !hasMemberErrors;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (isAtLimit) return;
        if (!validate()) return;

        setLoading(true);

        const payload = {
            familyName: familyName.trim(),
            members: members.map((m) => m.trim()),
        };

        AdminService.addFamily(payload)
            .then(() => {
                setLoading(false);
                dispatch(refreshDataGuest());
                setFamilyName("");
                setMembers(["", ""]);
                setSuccessMessage("Obitelj je uspješno dodana.");
                setTimeout(() => setSuccessMessage(""), 3000);
            })
            .catch((err) => {
                const message = err.response?.data?.message || "";
                if (message.includes("already exists")) {
                    setInvalidCredentials("Obitelj s tim nazivom već postoji.");
                } else if (message.includes("guest limit")) {
                    setInvalidCredentials("Dosegnut je limit gostiju za besplatnu pretplatu.");
                } else if (message.includes("between 2 and 15")) {
                    setInvalidCredentials("Obitelj mora imati između 2 i 15 članova.");
                } else {
                    setInvalidCredentials("Greška prilikom dodavanja obitelji.");
                }
                setLoading(false);
            });
    };

    return (
        <div className="flex justify-center pt-10 pb-10 lg:ml-[300px] overflow-y-auto">
            <div className="relative p-4 rounded-lg w-full max-w-2xl mx-auto">
                <h2 className="text-2xl font-semibold text-center mb-5 flex items-center justify-center">
                    <FaUsers className="mr-2 text-gray-700" /> Dodaj obitelj
                </h2>
                <div className="bg-blue-50 border border-blue-100 rounded-xl p-4 mb-7 text-sm text-gray-700 leading-relaxed">
                    <p className="font-semibold text-gray-800 mb-2">Kako funkcionira obiteljska pozivnica?</p>
                    <ul className="space-y-1.5 list-disc pl-4">
                        <li>Svi članovi obitelji dijele <strong>jednu pozivnicu</strong> (jedan link).</li>
                        <li><strong>Bilo tko iz obitelji</strong> koji otvori link može potvrditi dolazak za sve članove — savjetujte primatelja da se dogovori s ukućanima prije potvrde.</li>
                        <li>Obitelj dijeli <strong>jedan stol</strong>. Rezervira se onoliko mjesta koliko članova potvrdi dolazak.</li>
                        <li>Ako član promijeni odluku i ne dolazi, njegovo mjesto se automatski oslobađa.</li>
                    </ul>
                </div>

                {isAtLimit && (
                    <UpgradePrompt
                        reason="guest"
                        currentCount={guests.length}
                        limit={GUEST_FREE_LIMIT}
                    />
                )}

                <form
                    onSubmit={handleSubmit}
                    className={`flex flex-col space-y-7 mb-10 ${isAtLimit ? "opacity-50 pointer-events-none select-none" : ""}`}
                >
                    <fieldset disabled={isAtLimit} className="flex flex-col space-y-6">
                        {/* Family name */}
                        <div className="flex flex-col">
                            <label className="font-semibold flex items-center mb-1">
                                Naziv obitelji *
                            </label>
                            <input
                                type="text"
                                maxLength={60}
                                autoFocus={!isAtLimit}
                                value={familyName}
                                onChange={handleFamilyNameChange}
                                placeholder="npr. Obitelj Marić"
                                className={`inputWedding !pl-4 ${errors.familyName ? "!border-red-500" : ""}`}
                            />
                            {errors.familyName && (
                                <p className="text-red-500 text-sm mt-1">{errors.familyName}</p>
                            )}
                            <p className="text-xs text-gray-500 mt-1">
                                Koristite razlikovni naziv ako imate više obitelji s istim prezimenom.
                            </p>
                        </div>

                        {/* Members list */}
                        <div className="flex flex-col">
                            <label className="font-semibold flex items-center mb-2">
                                Članovi obitelji * <span className="ml-2 text-sm text-gray-500 font-normal">({members.length}/15)</span>
                            </label>

                            <div className="flex flex-col space-y-3">
                                {members.map((member, index) => (
                                    <div key={index} className="flex flex-col">
                                        <div className="flex gap-2 items-center">
                                            <input
                                                type="text"
                                                maxLength={50}
                                                value={member}
                                                onChange={(e) => handleMemberChange(index, e.target.value)}
                                                placeholder={`Ime i prezime ${index + 1}. člana`}
                                                className={`inputWedding !pl-4 flex-1 ${errors.members[index] ? "!border-red-500" : ""}`}
                                            />
                                            {members.length > 2 && (
                                                <button
                                                    type="button"
                                                    onClick={() => handleRemoveMember(index)}
                                                    className="flex items-center justify-center w-10 h-10 bg-white border border-red-200 text-red-500 rounded-lg hover:bg-red-50 transition flex-shrink-0"
                                                    title="Ukloni člana"
                                                >
                                                    <FaTimes className="text-sm" />
                                                </button>
                                            )}
                                        </div>
                                        {errors.members[index] && (
                                            <p className="text-red-500 text-sm mt-1">{errors.members[index]}</p>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {members.length < 15 && (
                                <button
                                    type="button"
                                    onClick={handleAddMember}
                                    className="mt-3 flex items-center justify-center gap-2 py-2 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-rose-300 hover:text-rose-500 hover:bg-rose-50/50 transition"
                                >
                                    <FaPlus className="text-xs" /> Dodaj još jednog člana
                                </button>
                            )}
                        </div>

                        {invalidCredentials && (
                            <p className="bg-red-100 rounded-lg p-2 text-red-700 text-center my-4">
                                {invalidCredentials}
                            </p>
                        )}

                        {successMessage && (
                            <p className="bg-green-100 rounded-lg p-2 text-green-700 text-center my-4">
                                {successMessage}
                            </p>
                        )}

                        <button
                            type="submit"
                            disabled={loading || isAtLimit}
                            className="submitButton w-full"
                        >
                            {loading ? <LoadingForButton /> : "Dodaj obitelj"}
                        </button>
                    </fieldset>
                </form>
            </div>
        </div>
    );
};

export default AddFamily;
